import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { axiosInstance } from '../App';

/**
 * Notification Bell — Feb 2026 enhancements
 * ──────────────────────────────────────────
 *  • Red pulsing badge with the unread count.
 *  • One-shot bounce animation when the count INCREASES (new arrival).
 *  • A dedicated "🔥 urgent" rendering for ``kind === 'quotation_ready'``.
 *  • The badge auto-clears (marks as read) when the user lands on
 *    ``/sales-quotations`` — closing the loop the user actually cares
 *    about. Clicking the notification individually also clears it.
 */
const NotificationBell = () => {
  const location = useLocation();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [bounce, setBounce] = useState(false);
  const dropRef = useRef(null);
  const prevCount = useRef(0);

  const reload = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/notifications/count');
      const c = r.data?.unread || 0;
      setCount(c);
      // Trigger the one-shot bounce only when count INCREASES from prior value.
      if (c > prevCount.current && prevCount.current >= 0) {
        setBounce(true);
        setTimeout(() => setBounce(false), 1200);
      }
      prevCount.current = c;
    } catch {}
  }, []);

  const loadItems = async () => {
    try {
      const r = await axiosInstance.get('/notifications?unread_only=true&limit=8');
      setItems(r.data || []);
    } catch {}
  };

  // Mark a single notification read locally + on server, then refresh count.
  const markRead = async (id) => {
    try {
      await axiosInstance.post(`/notifications/${id}/read`);
    } catch {}
    reload();
  };

  // Auto-clear quotation_ready notifications when the sales rep
  // actually opens /sales-quotations. Best-effort — uses the bulk
  // /read-all endpoint scoped by kind if available, else iterates.
  useEffect(() => {
    if (location.pathname !== '/sales-quotations') return;
    (async () => {
      try {
        // Pull all unread quotation_ready notifications for this user.
        const r = await axiosInstance.get('/notifications?unread_only=true&kind=quotation_ready&limit=50');
        const list = r.data || [];
        await Promise.all(list.map((n) => axiosInstance.post(`/notifications/${n.id}/read`).catch(() => null)));
        reload();
      } catch {}
    })();
  }, [location.pathname, reload]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
  }, [reload]);

  useEffect(() => {
    const onClick = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = () => {
    setOpen(!open);
    if (!open) loadItems();
  };

  // Detect if there's an unread quotation_ready in the buffer for the special
  // "urgent" styling on the bell itself (used while dropdown is closed too).
  const [hasQuotationReady, setHasQuotationReady] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await axiosInstance.get('/notifications?unread_only=true&kind=quotation_ready&limit=1');
        setHasQuotationReady((r.data || []).length > 0);
      } catch {}
    })();
  }, [count]);

  return (
    <div className="relative" ref={dropRef}>
      <button
        onClick={toggle}
        data-testid="notification-bell"
        className={`relative p-2 rounded-lg hover:bg-gray-100 transition ${bounce ? 'animate-bounce' : ''}`}
        aria-label="Notifications"
      >
        <Bell size={20} className={hasQuotationReady ? 'text-rose-600' : 'text-gray-700'} />
        {count > 0 && (
          <>
            {/* Pulsing red dot (always visible while unread). */}
            <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4" aria-hidden="true">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-4 w-4 bg-rose-600"></span>
            </span>
            {/* Count badge sits on top of the dot. */}
            <span
              data-testid="notification-bell-count"
              className="absolute -top-1 -right-1 bg-rose-600 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-bold ring-2 ring-white shadow"
            >
              {count > 99 ? '99+' : count}
            </span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-[28rem] overflow-y-auto"
              data-testid="notification-dropdown">
          <div className="p-3 border-b font-semibold text-sm flex items-center justify-between">
            <span>Notifications · {count} unread</span>
            <Link to="/notifications" onClick={() => setOpen(false)} className="text-xs text-purple-600 hover:underline">
              View all
            </Link>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">No new notifications</div>
          ) : (
            items.map((n) => {
              const isQuotationReady = n.kind === 'quotation_ready';
              return (
                <Link
                  key={n.id}
                  to={n.link || '/notifications'}
                  onClick={() => { setOpen(false); markRead(n.id); }}
                  className={`block px-3 py-2 border-b last:border-b-0 transition ${
                    isQuotationReady
                      ? 'bg-rose-50 border-l-4 border-l-rose-500 hover:bg-rose-100'
                      : 'hover:bg-gray-50'
                  }`}
                  data-testid={`notification-item-${n.kind || 'generic'}`}
                >
                  <div className="flex items-start gap-2">
                    {isQuotationReady ? (
                      <span className="text-base mt-0.5" aria-hidden="true">🔥</span>
                    ) : (
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        n.severity === 'critical' ? 'bg-red-500' :
                        n.severity === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
                      }`} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${isQuotationReady ? 'text-rose-900' : ''}`}>{n.title}</p>
                      <p className="text-xs text-gray-700 whitespace-pre-line line-clamp-3">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </Link>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
